// AnalyticsContext.tsx - SIN VALIDACIONES DE RANGO
// contexts/AnalyticsContext.tsx
import { createContext, useContext, useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { ANALYTICS_API_URL } from '../constants/config';
import type { 
  CompleteAnalytics, 
  AnalyticsFilters
} from '../types/analytics';
import { getAndSanitize } from '../shared/utils/sanitization';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("America/Argentina/Buenos_Aires");

const TIMEZONE = "America/Argentina/Buenos_Aires";

interface AnalyticsContextType {
  analytics: CompleteAnalytics;
  filters: AnalyticsFilters;
  isLoading: boolean;
  error: Error | null;
  setFilters: (filters: AnalyticsFilters) => void;
  exportAnalytics: (format: 'json' | 'csv' | 'xlsx', customFilters?: Partial<AnalyticsFilters>) => Promise<void>;
  isAuthorized: boolean;
  setAuthorized: (value: boolean) => void;
  statistics?: {
    usersStats?: Array<{
      username: string;
      totalDeposits: number;
      totalWithdraws: number;
    }>;
  };
}

const defaultAnalytics: CompleteAnalytics = {
  users: {
    total: 0,
    byAccount: {},
    averageLoadAmount: 0,
    averageLoadsPerUser: 0,
    usersCreatedInRange: 0,
    usersWithoutCode: 0,
    averageLoadsPerUserCreatedInRange: 0,
    averageLoadAmountRangeUsers: 0,
    highestLoadRangeUsers: { amount: 0, username: '' },
    segmentsActivityInRange: {
      whales: { loadedInRangeCount: 0, totalLoadsInRange: 0, totalAmountInRange: 0, avgLoadsInRange: 0 },
      dolphins: { loadedInRangeCount: 0, totalLoadsInRange: 0, totalAmountInRange: 0, avgLoadsInRange: 0 },
      fish: { loadedInRangeCount: 0, totalLoadsInRange: 0, totalAmountInRange: 0, avgLoadsInRange: 0 },
      inactive: { loadedInRangeCount: 0, totalLoadsInRange: 0, totalAmountInRange: 0, avgLoadsInRange: 0 }
    },
    highestLoad: { amount: 0, username: '' },
    mostActiveDay: { day: '', count: 0 },
    mostActiveWeek: { week: 0, count: 0 },
    trends: [],
    topLoaders: [],
    topWithdrawers: [],
    topLoadAmounts: [],
    topWithdrawAmounts: [],
    mostActiveHour: { hour: 0, count: 0 },
    usersWithDeposits: {
      count: 0,
      percentage: 0
    },
    userDepositDetails: [],
    segmentation: {
      whales: { count: 0, totalValue: 0, percentage: 0 },
      dolphins: { count: 0, totalValue: 0, percentage: 0 },
      fish: { count: 0, totalValue: 0, percentage: 0 },
      inactive: { count: 0, totalValue: 0, percentage: 0 }
    },
    timeToFirstDeposit: {
      averageHours: 0,
      totalUsersCreated: 0,
      totalUsersWhoDeposited: 0,
      conversionRate: 0,
      within24Hours: { count: 0, percentage: 0 },
      moreThan24Hours: { count: 0, percentage: 0 },
      conversationByDay: []
    },
    streakAnalysis: {
      longestStreak: { username: '', days: 0 },
      averageStreak: 0,
      superActiveUsers: { count: 0, percentage: 0, threshold: 0, details: [] },
      averageDaysBetweenFirstLast: 0,
      returningUsers: { count: 0, percentage: 0, inactivityDays: 0, details: [] }
    },
    depositWithdrawRatio: [],
    progressiveAmounts: {
      growthPattern: { count: 0, percentage: 0 },
      declinePattern: { count: 0, percentage: 0 },
      volatilePattern: { count: 0, percentage: 0 },
      stablePattern: { count: 0, percentage: 0 }
    },
    diversityAnalysis: {
      depositOnly: { count: 0, percentage: 0 },
      withdrawOnly: { count: 0, percentage: 0 },
      mixed: { count: 0, percentage: 0 },
      averageOperationTypes: 0,
      monoFunction: { count: 0, percentage: 0 },
      multiFunction: { count: 0, percentage: 0 }
    },
    personalTimePatterns: {
      nocturnalUsers: { count: 0, percentage: 0 },
      diurnalUsers: { count: 0, percentage: 0 },
      averageTimeConsistency: 0,
      topPersonalHours: []
    }
  },
  requests: {
    total: 0,
    loads: { total: 0, approved: 0, rejected: 0 },
    withdraws: { total: 0, approved: 0, rejected: 0 },
    processing: { automatic: 0, manual: 0, reprocessed: 0 },
    trends: [],
    rejectionReasons: [],
    avgDepositsPerDay: 0,
    avgWithdrawsPerDay: 0,
    mostActiveLoadHour: { hour: 0, count: 0 },
    mostActiveWithdrawHour: { hour: 0, count: 0 },
    hourlyActivity: [],
    rejectionTrends: [],
    dailyActivity: [],
    weeklyActivity: [],
    monthlyActivity: []
  },
  wallet: {
    transfers: { total: 0, validated: 0, pending: 0 },
    amounts: { total: 0, validated: 0, pending: 0 },
    platforms: [],
    volumeTrend: [],
    validationTrend: []
  },
  finance: {
    totals: { income: 0, expenses: 0, netBalance: 0, totalOperations: 0, totalBonusGiven: 0, bonusCostPercentage: 0 },
    trends: [],
    forecast: [],
    waterfall: [],
    anomalies: []
  },
  gamification: {
    bonusTotals: { totalBonusGiven: 0, totalPointsGiven: 0, bonusApplicationCount: 0, averageBonusPerApplication: 0, bonusCostPercentage: 0, topRules: [], byType: { percentage: { amount: 0, count: 0 }, fixedAmount: { amount: 0, count: 0 }, points: { amount: 0, count: 0 } }, bonusTrend: [] },
    rollover: { activeCount: 0, completedCount: 0, cancelledCount: 0, avgFulfillmentTimeHours: 0, fulfillmentRate: 0, avgRolloverMultiplier: 0, totalRolloverAmount: 0, cancelReasonBreakdown: [], rolloverTrend: [] },
    antiLavado: { complianceRate: 100, totalRolloversEvaluated: 0, suspiciousPatterns: [], bonusAbuseDetection: { overCapCount: 0, totalEvaluated: 0, abuseRate: 0 }, caminoBUsageRate: 0, caminoBCount: 0 },
    revenueImpact: { bonusROI: 0, avgDepositsWithBonus: 0, avgDepositsWithoutBonus: 0, costPerRetainedUser: 0, bonusEfficiency: 0, retentionUplift: 0 },
    userEngagement: { levelDistribution: [], avgStreakLength: 0, streakBreakRate: 0, levelProgressionRate: 0, totalGamifiedUsers: 0 },
    referrals: { totalReferrals: 0, activeReferrers: 0, referralConversionRate: 0, avgBonusPerReferral: 0, topReferrers: [], referralTrend: [] },
    pointsEconomy: { totalEarned: 0, totalRedeemed: 0, redemptionRate: 0, pointsVelocity: 0, inflationIndex: 0, currentOutstanding: 0, earnedTrend: [] }
  }
};

// SIN RESTRICCIONES DE FECHA - desde primer registro hasta hoy
const defaultFilters: AnalyticsFilters = {
  dateRange: {
    start: dayjs().tz(TIMEZONE).startOf("day").toISOString(),
    end: dayjs().tz(TIMEZONE).endOf("day").toISOString(),
  },
  accountName: undefined,
  token: getAndSanitize('token') || '',
  adminId: 'all',
  platform: undefined,
  type: undefined,
  status: undefined
};

export const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

interface AnalyticsProviderProps {
  children: React.ReactNode;
  initialFilters?: Partial<AnalyticsFilters>;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ 
  children, 
  initialFilters = {} 
}) => {
  const queryClient = useQueryClient();
  const [isAuthorized, setIsAuthorized] = useState<boolean>(true);

  const [filters, setFilters] = useState<AnalyticsFilters>({
    ...defaultFilters,
    ...initialFilters
  });
  const [error, setError] = useState<Error | null>(null);

  const { 
    data: analytics = defaultAnalytics, 
    isLoading, 
    error: queryError 
  } = useQuery({
    queryKey: ['analytics', filters],
    queryFn: async (): Promise<CompleteAnalytics> => {
      const currentUserAdminId = getAndSanitize('adminId') || getAndSanitize('userId') || '';
      const token = getAndSanitize('token') || '';
      
      
      if (!token) {
        console.error('Analytics request failed: token is required');
        throw new Error('Authentication token is required for analytics');
      }
      
      try {
        const { token: _, ...paramsWithoutToken } = filters;
        
        let queryAdminId = filters.adminId;
        if (!queryAdminId) {
          queryAdminId = currentUserAdminId;
        }
        
        
        const response = await axios.get<{
          success: boolean;
          data: CompleteAnalytics;
        }>(`${ANALYTICS_API_URL}/api/analytics`, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'X-Admin-ID': currentUserAdminId
          },
          params: {
            ...paramsWithoutToken,
            adminId: queryAdminId
          }
        });


        // LOG: Streak Analysis específico

        if (!response.data.success || !response.data.data) {
          throw new Error('Failed to fetch analytics data');
        }

        const d = response.data.data;
        console.log('[Analytics API] Response keys:', Object.keys(d));
        console.log('[Analytics API] finance:', JSON.stringify(d.finance?.totals));
        console.log('[Analytics API] requests:', JSON.stringify({ total: d.requests?.total, loads: d.requests?.loads?.total, withdraws: d.requests?.withdraws?.total }));
        console.log('[Analytics API] wallet:', JSON.stringify({ total: d.wallet?.transfers?.total, validated: d.wallet?.transfers?.validated }));
        console.log('[Analytics API] users.total:', d.users?.total, 'topLoaders:', d.users?.topLoaders?.length);

        return d;
      } catch (error) {
        console.error('❌ [Analytics Query] Unlimited request failed:', error);
        
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          setIsAuthorized(false);
        }
        throw error;
      }
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: 1,
    enabled: true,
    placeholderData: undefined,
    notifyOnChangeProps: ['data', 'error', 'isLoading']
  });

  const setAuthorized = (value: boolean): void => {
    setIsAuthorized(value);
    queryClient.invalidateQueries({ queryKey: ['analytics'] });
  };

  const exportAnalytics = async (format: 'json' | 'csv' | 'xlsx', customFilters?: Partial<AnalyticsFilters>): Promise<void> => {
    try {
      const exportFilters = {
        ...filters,
        ...customFilters
      };
      
      const response = await axios.get(`${ANALYTICS_API_URL}/api/analytics/export`, {
        headers: { 
          Authorization: `Bearer ${exportFilters.token}`,
          'X-Admin-ID': exportFilters.adminId
        },
        params: {
          ...exportFilters,
          format
        },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], {
        type: format === 'json' ? 'application/json' :
              format === 'csv' ? 'text/csv' :
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `analytics-export-${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      setError(err as Error);
      throw err;
    }
  };

  // SIN VALIDACIONES - acepta cualquier rango de fechas
  const handleSetFilters = useCallback(async (newFilters: Partial<AnalyticsFilters>): Promise<void> => {
    try {

      setFilters(prevFilters => {
        const updatedFilters: AnalyticsFilters = {
          ...prevFilters,
          ...newFilters,
          dateRange: {
            ...prevFilters.dateRange,
            ...(newFilters.dateRange || {})
          },
          timestamp: Date.now()
        };


        return updatedFilters;
      });

    } catch (err) {
      console.error('Error updating unlimited filters:', err);
      setError(err as Error);
      throw err;
    }
  }, []);

  return (
    <AnalyticsContext.Provider 
      value={{ 
        analytics: analytics || defaultAnalytics,
        filters, 
        isLoading, 
        error: queryError as Error || error,
        setFilters: handleSetFilters,
        exportAnalytics,
        isAuthorized,
        setAuthorized,
        statistics: {
          usersStats: (analytics?.users?.topLoaders || []).map(loader => ({
            username: loader.username,
            totalDeposits: loader.amount,
            totalWithdraws: analytics?.users?.topWithdrawAmounts?.find(
              w => w.username === loader.username
            )?.amount || 0
          }))
        }
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = (): AnalyticsContextType => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};

export default AnalyticsProvider;